import React, { useState, useEffect } from 'react';

const EmbedModal = ({ isOpen, onClose, embedCode, options = {}, onOptionsChange, mode = 'timeline' }) => {
  // Determine default height based on mode
  const defaultHeight = mode === 'map' ? '500px' : '850px';

  const [localOptions, setLocalOptions] = useState({
    width: options.width || '100%',
    height: options.height || defaultHeight,
    ...(mode === 'timeline' ? {
      h: options.h || '700',
      ts: options.ts || '100'
    } : {})
  });

  const [previewWidth, setPreviewWidth] = useState(800);
  const [useCustomWidth, setUseCustomWidth] = useState(false);

  useEffect(() => {
    const newDefaultHeight = mode === 'map' ? '500px' : '850px';
    setLocalOptions({
      width: options.width || '100%',
      height: options.height || newDefaultHeight,
      ...(mode === 'timeline' ? {
        h: options.h || '700',
        ts: options.ts || '100'
      } : {})
    });
  }, [options, mode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    // Append 'px' to height input
    if (name === 'height' && value) {
      newValue = `${value}px`;
    }

    const newOptions = { ...localOptions, [name]: newValue };
    setLocalOptions(newOptions);
    if (onOptionsChange) {
      onOptionsChange(newOptions);
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50" style={{ backdropFilter: 'blur(3px)' }}>
      <div className="bg-white rounded-lg shadow-xl p-6 w-[95%] h-[90%] mx-4 flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-800">
            {mode === 'map' ? 'Harita Embed Kodu' : 'Embed Kodu'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex flex-1 gap-6 overflow-hidden">
          {/* Left side - Configuration and Code */}
          <div className="w-1/3 flex flex-col overflow-y-auto pr-4">
            <p className="text-gray-600 mb-4">
              {mode === 'map'
                ? 'Bu kodu web sitenize veya blogunuza ekleyerek haritayı gömebilirsiniz. Bu kod doğrudan herhangi bir web sayfasında çalışacak şekilde tasarlanmıştır.'
                : 'Bu kodu web sitenize veya blogunuza ekleyerek bu etkinliği gömebilirsiniz. Bu kod doğrudan herhangi bir web sayfasında çalışacak şekilde tasarlanmıştır.'
              }
            </p>

            <h4 className="text-md font-semibold text-gray-800 mb-2">Özelleştirme Seçenekleri:</h4>

            <div className="grid grid-cols-1 gap-4 mb-4">
              <div className="flex flex-col">
                <label className="text-sm text-gray-600 mb-1">Genişlik:</label>
                <input
                  type="text"
                  name="width"
                  value={localOptions.width}
                  onChange={handleInputChange}
                  className="border border-gray-300 rounded-md p-2 text-sm"
                  placeholder="Örn: 100% veya 800px"
                />
              </div>


              <div className="flex flex-col">
                <label className="text-sm text-gray-600 mb-1">Yükseklik:</label>
                <input
                  type="number"
                  name="height"
                  value={parseInt(localOptions.height)} // Display only the number part
                  onChange={handleInputChange}
                  className="border border-gray-300 rounded-md p-2 text-sm"
                  placeholder={mode === 'map' ? 'Örn: 500' : 'Örn: 850'}
                />
              </div>

              {mode === 'timeline' && (
                <>
                  <div className="flex flex-col">
                    <label className="text-sm text-gray-600 mb-1">İç Yükseklik (h):</label>
                    <input
                      type="number"
                      name="h"
                      value={localOptions.h}
                      onChange={handleInputChange}
                      className="border border-gray-300 rounded-md p-2 text-sm"
                      placeholder="Örn: 700"
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm text-gray-600 mb-1">Zaman Atlama (ts):</label>
                    <input
                      type="number"
                      name="ts"
                      value={localOptions.ts}
                      onChange={handleInputChange}
                      className="border border-gray-300 rounded-md p-2 text-sm"
                      placeholder="Örn: 100"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="relative flex-1">
              <textarea
                className="w-full h-full p-3 border border-gray-300 rounded-md font-mono text-sm bg-gray-50"
                readOnly
                value={embedCode}
              />
              <button
                onClick={async () => {
                  try {
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                      await navigator.clipboard.writeText(embedCode);
                    } else {
                      const textArea = document.createElement("textarea");
                      textArea.value = embedCode;
                      textArea.style.position = "fixed";
                      textArea.style.left = "-9999px";
                      textArea.style.top = "0";
                      document.body.appendChild(textArea);
                      textArea.focus();
                      textArea.select();
                      try {
                        document.execCommand('copy');
                      } catch (err) {
                        console.error('Fallback: Oops, unable to copy', err);
                        alert('Kopyalama başarısız oldu. Lütfen manuel olarak kopyalayın.');
                      }
                      document.body.removeChild(textArea);
                    }
                  } catch (err) {
                    console.error('Async: Could not copy text: ', err);
                    alert('Kopyalama başarısız oldu. Lütfen manuel olarak kopyalayın.');
                  }
                }}
                className="absolute top-2 right-2 bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-md text-sm font-medium transition-colors"
              >
                Kopyala
              </button>
            </div>
          </div>

          {/* Right side - Preview */}
          <div className="w-2/3 flex flex-col">
            <div className="flex items-center gap-4 mb-2">
              <h4 className="text-md font-semibold text-gray-800">Önizleme:</h4>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="useCustomWidth"
                  checked={useCustomWidth}
                  onChange={(e) => setUseCustomWidth(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <label htmlFor="useCustomWidth" className="text-sm text-gray-600">Önizleme Genişliği</label>
                {useCustomWidth && (
                  <input
                    type="number"
                    value={previewWidth}
                    onChange={(e) => setPreviewWidth(Number(e.target.value))}
                    className="w-20 border border-gray-300 rounded-md p-1 text-sm"
                    placeholder="Genişlik"
                  />
                )}
              </div>
            </div>
            <div
              className="flex-1 border border-gray-300 rounded-md p-4 overflow-auto bg-gray-50"
              style={{ width: useCustomWidth ? `${previewWidth}px` : '100%' }}
            >
              <div dangerouslySetInnerHTML={{ __html: embedCode }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmbedModal; 